import { useEffect, useRef, useCallback, useState } from 'react';
import { Canvas } from 'fabric';

const PADDING = 40;

/**
 * Fits and centers the design within the available container space.
 * Recalculates the fitted zoom whenever the container is resized.
 */
export const useCanvasFit = (
  canvas: Canvas | null,
  canvasContainerRef: React.RefObject<HTMLDivElement | null>,
  designWidth: number,
  designHeight: number
) => {
  const [baseZoom, setBaseZoom] = useState(1);
  const observerRef = useRef<ResizeObserver | null>(null);

  const fitViewport = useCallback(() => {
    if (!canvas || !canvasContainerRef.current) return;

    const container = canvasContainerRef.current;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;

    if (containerW <= 0 || containerH <= 0) return;

    const availableW = containerW - PADDING * 2;
    const availableH = containerH - PADDING * 2;
    const scale = Math.min(availableW / designWidth, availableH / designHeight);

    const offsetX = (containerW - designWidth * scale) / 2;
    const offsetY = (containerH - designHeight * scale) / 2;

    canvas.setDimensions({ width: containerW, height: containerH });
    canvas.setViewportTransform([scale, 0, 0, scale, offsetX, offsetY]);

    setBaseZoom(scale);
  }, [canvas, canvasContainerRef, designWidth, designHeight]);

  useEffect(() => {
    if (!canvasContainerRef.current) return;

    let rafId: number;
    observerRef.current = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => fitViewport());
    });
    observerRef.current.observe(canvasContainerRef.current);

    // The initial fit runs after loadProject to avoid rendering old objects
    // with the new dimensions.
    return () => {
      cancelAnimationFrame(rafId);
      observerRef.current?.disconnect();
    };
  }, [canvasContainerRef, fitViewport]);

  return { baseZoom, fitViewport };
};
